import React from "react";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github.css";

// MUI Icons
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import styles from "./ChatWorkspace.module.scss";
import EnquiryList from "../AIChat/AIComponents/EnquiryList";
import EnquiryResponseCard from "./EnquiryResponseCard";
import CreateEnquiryForm from "../AIChat/AIComponents/CreateEnquiryForm";
import AIResponseRenderer from "./AIResponseRenderer";

const ABYSS_LOGO = "/bolt-logo.png"; // kept for any legacy avatar use; primary logo is agent-2.gif

function formatTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return value;
  }
}

function isStructuredResponse(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (payload.type && ["table", "cards", "form", "message"].includes(payload.type)) return true;
  if (payload.ui_type) return true;
  return false;
}

/**
 * When we have a table UI from a tool, strip list/table blocks from the LLM text
 * so the data appears only once (in the table). Keeps the rest of the message.
 */
function stripDuplicateListAndTableFromText(text, hasTablePayload) {
  if (typeof text !== "string" || !text.trim()) return text;
  if (!hasTablePayload) return text;

  let out = text;
  // Remove markdown pipe tables (| col | col |)
  const tableRegex = /\|.+\|\s*\n\s*\|[-:\s|]+\|\s*\n(\|.+\|\s*\n?)*/g;
  out = out.replace(tableRegex, "\n");
  // Remove bullet/numbered list blocks (2+ items) so same data isn't shown as list then table
  out = out.replace(/(?:^|\n)(\s*[-*]\s+.+(\n\s*[-*]\s+.+)+)/gm, "\n");
  out = out.replace(/(?:^|\n)(\s*\d+\.\s+.+(\n\s*\d+\.\s+.+)+)/gm, "\n");
  out = out.replace(/\n{3,}/g, "\n\n").trim();
  return out;
}

function getStructuredPayload(message) {
  const toolPayload = message.tool_payload;
  if (!toolPayload || typeof toolPayload !== "object") return null;
  if (toolPayload.ui_type === "text" && String(toolPayload.content || "").startsWith("Tool error:")) return null;
  if (isStructuredResponse(toolPayload)) return toolPayload;
  const content = message.content;
  if (typeof content === "string" && content.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed.type === "string") return parsed;
    } catch (_) {}
  }
  return null;
}

export default function ChatMessage({
  message,
  onRegenerate,
  onContinue,
  onEdit,
  onCopy,
  onReact,
  conversationId,
  onFormSubmit,
  onSuggestionClick,
}) {
  const isUser = message.role === "user";
  const isTool = message.role === "tool";
  const isAssistant = !isUser && !isTool;
  const rowClass = isUser ? styles.userRow : styles.assistantRow;
  const bubbleClass = isUser
    ? `${styles.messageBubble} ${styles.userBubble}`
    : isAssistant
      ? styles.assistantMessage
      : styles.messageBubble;
  const uiPayload = message.tool_payload || null;
  const structuredPayload = getStructuredPayload(message);

  let body = null;
  if (isAssistant && structuredPayload) {
    const rawText = typeof message.content === "string" ? message.content.trim() : "";
    const hasTable = structuredPayload.type === "table" || structuredPayload.ui_type === "enquiry_list";
    const textOnly = stripDuplicateListAndTableFromText(rawText, !!hasTable);
    const hasText = textOnly.length > 0;
    const showTable = structuredPayload.show_table !== false;
    body = (
      <>
        {hasText && (
          <div className={styles.markdown} style={{ marginBottom: showTable ? "1rem" : 0 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
              {textOnly}
            </ReactMarkdown>
          </div>
        )}
        {showTable && (
          <AIResponseRenderer
            response={structuredPayload}
            conversationId={conversationId}
            onFormSubmit={onFormSubmit}
            onSuggestionClick={onSuggestionClick}
          />
        )}
        {!showTable && Array.isArray(structuredPayload.follow_ups) && structuredPayload.follow_ups.length > 0 && (
          <div className="mt-2">
            <span className="text-muted small">Suggested: </span>
            {structuredPayload.follow_ups.map((s, i) => (
              <button
                key={i}
                type="button"
                className="btn btn-outline-primary btn-sm me-1 mt-1"
                onClick={() => onSuggestionClick?.(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </>
    );
  } else if (uiPayload?.ui_type === "enquiry_list" && !structuredPayload) {
    body = (
      <EnquiryResponseCard
        data={uiPayload.data || []}
        title="Here are the enquiries I found"
      />
    );
  } else if (uiPayload?.ui_type === "create_enquiry_form" && !structuredPayload) {
    body = <CreateEnquiryForm fields={uiPayload.fields || []} />;
  } else if (isTool) {
    body = (
      <pre className="mb-0">
        <code>{message.content}</code>
      </pre>
    );
  } else {
    body = (
      <div className={styles.markdown}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
          {message.content || ""}
        </ReactMarkdown>
      </div>
    );
  }

  // Helper to render an icon button with tooltip
  const IconButton = ({ tip, icon, onClick, active, activeVariant }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip>{tip}</Tooltip>}>
      <button
        className={`${styles.iconBtn} ${active ? styles[activeVariant] : ""}`}
        onClick={onClick}
      >
        {icon}
      </button>
    </OverlayTrigger>
  );

  return (
    <div className={`${styles.messageRow} ${rowClass}`}>
      

      <div className={bubbleClass}>
        {/* Header: only for assistant/tool (no "You" or time for user) */}
        {isTool && (
          <div className={styles.messageHeader}>
            <span className={styles.senderName}>Tool</span>
            <span className={styles.timestamp}>{formatTime(message.created_at)}</span>
          </div>
        )}
        {isAssistant && (
          <div className={styles.assistantLabel}>
            <span className={styles.senderName}>Abyss</span>
            {message.model_used && (
              <Badge bg="light" text="dark" className={styles.modelBadge}>
                {message.model_used}
              </Badge>
            )}
          </div>
        )}

        {/* Message content */}
        {body}

        {/* Attachments */}
        {Array.isArray(message.attachments) && message.attachments.length > 0 && !isTool && (
          <div className={styles.attachments}>
            <div className={styles.attachmentsLabel}>Attachments</div>
            <div className="d-flex flex-wrap gap-2">
              {message.attachments.map((attachment) => (
                <Badge bg="light" text="dark" key={attachment.id || attachment.storage_path || attachment.name}>
                  {attachment.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons: for user always; for assistant only after typing is done (not streaming) */}
        {!isTool && !(isAssistant && message.status === "streaming") && (
          <div className={isUser ? styles.messageActionsUser : styles.messageActions}>
            <IconButton tip="Copy" icon={<ContentCopyIcon fontSize="small" />} onClick={() => onCopy?.(message)} />

            {isUser ? (
              <IconButton tip="Edit prompt" icon={<EditIcon fontSize="small" />} onClick={() => onEdit?.(message)} />
            ) : (
              <>
                <IconButton tip="Regenerate" icon={<RefreshIcon fontSize="small" />} onClick={() => onRegenerate?.(message)} />
                <IconButton tip="Continue" icon={<ArrowForwardIcon fontSize="small" />} onClick={() => onContinue?.(message)} />

                <div className={styles.reactionGroup}>
                  <IconButton
                    tip="Upvote"
                    icon={<ThumbUpIcon fontSize="small" />}
                    onClick={() => onReact?.(message, message.reaction === "up" ? null : "up")}
                    active={message.reaction === "up"}
                    activeVariant="activeUp"
                  />
                  <IconButton
                    tip="Downvote"
                    icon={<ThumbDownIcon fontSize="small" />}
                    onClick={() => onReact?.(message, message.reaction === "down" ? null : "down")}
                    active={message.reaction === "down"}
                    activeVariant="activeDown"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}